"use client";

import { useRouter } from "next/navigation";
import { CSSProperties, MouseEvent, useEffect, useRef, useState } from "react";
import { useT } from "@/app/translations/useT";
import { ProjectOverviewStats } from "@/app/types/project/ProjectOverview";
import { UStyles } from "@/app/utils/UStyles";
import FlowOrbNode from "./FlowOrbNode";
import { buildFlowNodes, FLOW_STAGES } from "./ProjectFlow.logic";
import styles from "./ProjectFlow.module.css";

type Props = {
  lang: string;
  stats: ProjectOverviewStats;
};

export default function ProjectFlow(props: Props) {
  const { lang, stats } = props;
  const T = useT();
  const router = useRouter();
  const stageRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    setIsMobile(query.matches);
    const listener = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, []);

  const nodes = buildFlowNodes(FLOW_STAGES, stats, T, isMobile);

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    stage.style.setProperty("--ry", `${px * 7}deg`);
    stage.style.setProperty("--rx", `${-py * 5}deg`);
  };

  const handleLeave = () => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    stage.style.setProperty("--ry", "0deg");
    stage.style.setProperty("--rx", "0deg");
  };

  const onNavigate = (path: string) => router.push(`/${lang}${path}`);

  return (
    <div className={styles.scene} onMouseMove={handleMove} onMouseLeave={handleLeave}>
      <div className={styles.nebula} />
      <div className={styles.grid} />
      <div ref={stageRef} className={styles.stage}>
        <svg className={styles.links} viewBox="0 0 100 100" preserveAspectRatio="none">
          {nodes.slice(0, -1).map((node, i) => {
            const next = nodes[i + 1];
            const active = node.reached && next.reached;
            return (
              <line
                key={node.key}
                x1={node.position[0]}
                y1={node.position[1]}
                x2={next.position[0]}
                y2={next.position[1]}
                className={UStyles.classNames(styles.link, active && styles.linkActive)}
              />
            );
          })}
        </svg>

        <div className={styles.packets}>
          {nodes.slice(0, -1).flatMap((node, i) => {
            const next = nodes[i + 1];
            if (!(node.reached && next.reached)) {
              return [];
            }
            return [0, 1, 2].map((k) => (
              <span
                key={`${node.key}-${k}`}
                className={styles.packet}
                style={
                  {
                    "--fx": `${node.position[0]}%`,
                    "--fy": `${node.position[1]}%`,
                    "--tx": `${next.position[0]}%`,
                    "--ty": `${next.position[1]}%`,
                    animationDelay: `${k * 0.9}s`,
                  } as CSSProperties
                }
              />
            ));
          })}
        </div>

        {nodes.map((node, i) => (
          <FlowOrbNode key={node.key} node={node} index={i} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  );
}
